import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { adminApi } from "../../utils/axiosConfig";
import {
  FaSignOutAlt,
  FaHome,
  FaPlusCircle,
  FaClipboardList,
  FaBolt,
  FaChartLine,
  FaBoxes,
  FaEnvelope,
  FaBars,
  FaTimes,
  FaQuestionCircle,
  FaHandScissors,
  FaBook,
  FaUsers
} from "react-icons/fa";
import { MdAdminPanelSettings } from "react-icons/md";

export default function AdminNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const activePath = location.pathname;

  const [isOpen, setIsOpen] = useState(false);

  const isActive = (path) => (activePath.startsWith(path) ? "active" : "");

  const handleLogout = async () => {
    try {
      await adminApi.post('/api/admin/logout');
      // Supprimer le token admin du localStorage
      localStorage.removeItem('adminToken');
      navigate("/admin/login");
    } catch (error) {
      console.error("Erreur lors de la déconnexion", error);
      // Même en cas d'erreur, supprimer le token et rediriger
      localStorage.removeItem('adminToken');
      navigate("/admin/login");
    }
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleLinkClick = () => setIsOpen(false);

  return (
    <>
      <style>
        {`
          :root {
            --primary: #6366f1;
            --primary-dark: #4f46e5;
            --dark: #1e293b;
            --light: #f8fafc;
            --gray: #94a3b8;
            --success: #10b981;
            --danger: #ef4444;
          }

          .admin-navbar {
            width: 100%;
            font-family: 'Inter', sans-serif;
            background: linear-gradient(135deg, var(--dark), #0f172a);
            box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
            position: sticky;
            top: 0;
            z-index: 1000;
          }

          .navbar-container {
            display: flex;
            flex-direction: column;
            width: 100%;
            position: relative;
          }

          .navbar-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 2rem;
            height: 70px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .admin-brand {
            display: flex;
            align-items: center;
            gap: 10px;
            color: white;
            font-size: 1.25rem;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .admin-brand svg {
            color: var(--primary);
            font-size: 1.5rem;
          }

          .logout-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(239, 68, 68, 0.1);
            color: var(--danger);
            border: 1px solid var(--danger);
            padding: 0.5rem 1rem;
            border-radius: 6px;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            white-space: nowrap;
          }

          .logout-btn:hover {
            background: var(--danger);
            color: white;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          }

          /* Hamburger button */
          .hamburger-btn {
            display: none;
            background: none;
            border: none;
            color: white;
            font-size: 1.5rem;
            cursor: pointer;
            z-index: 1100;
          }

          .nav-links {
            display: flex;
            overflow-x: auto;
            scrollbar-width: none;
            padding: 0 1rem;
            gap: 18px;
          }

          .nav-links::-webkit-scrollbar {
            display: none;
          }

          .nav-link {
            display: flex;
            align-items: center;
            gap: 6px;
            color: var(--gray);
            text-decoration: none;
            font-weight: 500;
            font-size: 0.8125rem;
            padding: 0.8rem 0.8rem;
            position: relative;
            white-space: nowrap;
            transition: all 0.3s ease;
          }

          .nav-link svg {
            font-size: 0.875rem;
          }

          .nav-link:hover {
            color: white;
            background: rgba(255, 255, 255, 0.05);
          }

          .nav-link.active {
            color: white;
            font-weight: 600;
          }

          .nav-link.active::after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 60%;
            height: 3px;
            background: var(--primary);
            border-radius: 3px 3px 0 0;
          }

          .nav-link.active svg {
            color: var(--primary);
          }

          @media (max-width: 768px) {
            .navbar-top {
              padding: 0 1rem;
            }

            .admin-brand {
              font-size: 1rem;
            }

            /* Show hamburger */
            .hamburger-btn {
              display: block;
            }

            /* Mobile nav-links container */
            .nav-links {
              flex-direction: column;
              background: var(--dark);
              position: absolute;
              top: 70px;
              left: 0;
              width: 100%;
              max-height: 0;
              overflow: hidden;
              transition: max-height 0.3s ease;
              border-bottom: 1px solid rgba(255,255,255,0.1);
              z-index: 1000;
              padding: 0;
            }

            .nav-links.open {
              max-height: 500px;
              overflow-y: auto;
              padding-bottom: 1rem;
            }

            .nav-link {
              padding: 0.7rem 1.5rem;
              border-top: 1px solid rgba(255, 255, 255, 0.1);
              white-space: normal;
              gap: 6px;
            }

            /* Logout button in nav */
            .logout-btn.mobile-logout {
              display: flex;
              margin: 0 2rem 1rem 2rem;
            }

            /* Logout button top in mobile, shown only if menu closed */
            .logout-btn.mobile-top {
              display: flex;
              margin-left: auto;
            }
          }

          @media (max-width: 480px) {
            .admin-brand span {
              display: none;
            }

            .logout-btn.mobile-top span {
              display: none;
            }

            .logout-btn.mobile-logout {
              padding: 0.5rem;
              border-radius: 50%;
              width: 36px;
              height: 36px;
              justify-content: center;
              margin: 0 1.5rem 1rem 1.5rem;
            }

            .nav-link {
              padding: 0.6rem 1.2rem;
              font-size: 0.75rem;
            }
          }
        `}
      </style>

      <header className="admin-navbar">
        <div className="navbar-container">
          <div className="navbar-top">
            <div className="admin-brand">
              <MdAdminPanelSettings />
              <span>Espace Admin</span>
            </div>

            {!isOpen && (
              <button
                onClick={handleLogout}
                className="logout-btn mobile-top"
                aria-label="Déconnexion"
              >
                <FaSignOutAlt />
                <span>Déconnexion</span>
              </button>
            )}

            <button
              className="hamburger-btn"
              onClick={toggleMenu}
              aria-label="Toggle navigation menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>

          <nav className={`nav-links ${isOpen ? "open" : ""}`}>
            <Link
              to="/admin/dashboard"
              className={`nav-link ${isActive("/admin/dashboard")}`}
              onClick={handleLinkClick}
            >
              <FaHome /> Accueil
            </Link>
            <Link
              to="/admin/ajout-produit"
              className={`nav-link ${isActive("/admin/ajout-produit")}`}
              onClick={handleLinkClick}
            >
              <FaPlusCircle /> Ajouter Produit
            </Link>
            <Link
              to="/admin/commandes"
              className={`nav-link ${isActive("/admin/commandes")}`}
              onClick={handleLinkClick}
            >
              <FaClipboardList /> Commandes
            </Link>

            <Link
              to="/admin/quick-orders"
              className={`nav-link ${isActive("/admin/quick-orders")}`}
              onClick={handleLinkClick}
            >
              <FaBolt /> Commande instantanée
            </Link>

            <Link
              to="/admin/produits"
              className={`nav-link ${isActive("/admin/produits")}`}
              onClick={handleLinkClick}
            >
              <FaBoxes /> Tous les Produits
            </Link>

            <Link
              to="/admin/newsletter"
              className={`nav-link ${isActive("/admin/newsletter")}`}
              onClick={handleLinkClick}
            >
              <FaEnvelope /> Newsletter
            </Link>

            <Link
              to="/admin/faq"
              className={`nav-link ${isActive("/admin/faq")}`}
              onClick={handleLinkClick}
            >
              <FaQuestionCircle /> FAQ
            </Link>

            <Link
              to="/admin/fabrication-artisanale"
              className={`nav-link ${isActive(
                "/admin/fabrication-artisanale"
              )}`}
              onClick={handleLinkClick}
            >
              <FaHandScissors /> Fabrication Artisanale
            </Link>

            <Link
              to="/admin/care-guide"
              className={`nav-link ${isActive("/admin/care-guide")}`}
              onClick={handleLinkClick}
            >
              <FaBook /> Blog/Entretien
            </Link>

            <Link
              to="/admin/codes-promo"
              className={`nav-link ${isActive("/admin/codes-promo")}`}
              onClick={handleLinkClick}
            >
              <FaChartLine /> Codes Promo
            </Link>

            <Link
              to="/admin/admins"
              className={`nav-link ${isActive("/admin/admins")}`}
              onClick={handleLinkClick}
            >
              <FaUsers /> Admins
            </Link>
          </nav>
        </div>
      </header>
    </>
  );
}
